<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, onMounted } from 'vue';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  import InfoIcon from '../common/InfoIcon.vue';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

  // Definitions
  const props = defineProps({
    configDetails: {
      type: Object,
      default: null
    }
  });

  const emit = defineEmits(['changedDetails', 'close']);

  const loading = ref(false);
  const error = ref('');
  const min = ref(props.configDetails?.channel_type === 'Voltage' ? props.configDetails?.min || 0 : props.configDetails?.min*1000 || 0 );
  const max = ref(props.configDetails?.channel_type === 'Voltage' ? props.configDetails?.max || 10 : props.configDetails?.max*1000 || 20);
  const scaleMin = ref(props.configDetails?.scale?.min || 0);
  const scaleMax = ref(props.configDetails?.scale?.max || 0);
  const offset = ref(props.configDetails?.scale?.offset || 0);
  const unit = ref(props.configDetails?.scale?.unit || '');
  const description = ref(props.configDetails?.description || '');
  const enabled = ref(props.configDetails?.enabled || false);
  const reportInterval = ref(props.configDetails?.reportInterval || 60);
  const electricalOutput = ref(props.configDetails?.channel_type === 'Voltage' ? 'V' : 'mA');

  // Tooltip content for different sections
  const tooltipContent = {
    'Channel Status': 'Enabled: collect and report data.\nDisabled: dont collect and report data.',
    'Report Interval': 'How often (in seconds) the sensor sends data',
    'Unit': 'The type of measurement the sensor is reporting.\n\nExample: "Celcius", "PSI", "RPM", "%", "V"',
    'Offset': 'This value is used to calibrate the sensor.\n\nExample: If the sensor is measuring 0.5°C when the actual value is 0°C, the offset would be -0.5.',
    'Signal Range min': `The minimum output of the sensor.\n\nExample: If the sensor is reporting 0-10${electricalOutput.value}, the input range would be min 0.`,
    'Signal Range max': `The maximum output of the sensor.\n\nExample: If the sensor is reporting 0-10${electricalOutput.value}, the input range would be max 10.`,
    'Unit min': 'The smallest possible value the sensor can measure.\n\nExample: If the sensor can measure between -20°C to 110°C, the min unit values would be -20.',
    'Unit max': 'The largest possible value the sensor can measure.\n\nExample: If the sensor can measure between -20°C to 110°C, the max unit values would be 110.',
    'Description': 'A personal description of the sensor connected to this channel.'
  };

  // Lifecycle hooks
  onMounted(async() => {
  });

  // Methods
  const removeChannel = async() => {
    loading.value = true;
    error.value = '';

    const newDetails = props.configDetails;

    newDetails.min = props.configDetails?.channel_type === 'Voltage' ? 0 : 0.004;
    newDetails.max = props.configDetails?.channel_type === 'Voltage' ? 10 : 0.02;
    newDetails.scale.min = 0;
    newDetails.scale.max = 0;
    newDetails.scale.offset = 0;
    newDetails.scale.unit = "";
    newDetails.description = "";
    newDetails.enabled = false;
    newDetails.report_interval = 60;

    // Emit the new config details and close the modal.
    emit('changedDetails', newDetails);
  }

  const handleSubmit = async() => {
    loading.value = true;
    error.value = '';

    const newDetails = props.configDetails;

    newDetails.min = min.value/1000;
    newDetails.max = max.value/1000;
    newDetails.scale.min = scaleMin.value;
    newDetails.scale.max = scaleMax.value;
    newDetails.scale.offset = offset.value;
    newDetails.scale.unit = unit.value;
    newDetails.description = description.value;
    newDetails.enabled = enabled.value;
    newDetails.report_interval = reportInterval.value;

    // Emit the new config details and close the modal.
    emit('changedDetails', newDetails);
  }

  const close = () => {
    loading.value = false;
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);

</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="bg-white dark:bg-gray-800 p-8 rounded-xl w-[600px] max-h-[calc(100vh-50px)] overflow-y-auto shadow-2xl" @mousedown="onContentMousedown">
      <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
      <h2 class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">
        Channel {{ configDetails?.channel || 'N/A' }} Configuration
      </h2>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Channel type -->
        <div>
          <label class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Channel Type</label>
          <p class="text-lg font-semibold text-mc-ink dark:text-on-dark mt-1">{{ configDetails?.channel_type || 'N/A' }}</p>
        </div>

        <!-- Enabled -->
        <div class="flex items-center justify-between">
          <span class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted flex items-center gap-2">
            Channel Status
            <InfoIcon :content="tooltipContent['Channel Status']" />
          </span>
          <div>
            <span v-if="enabled" class="text-sm font-medium text-mc-ink dark:text-on-dark">Enabled</span>
            <span v-else class="text-sm font-medium text-mc-ink dark:text-on-dark">Disabled</span>
            <button
              type="button"
              @click="enabled = !enabled"
              class="relative inline-flex h-6 w-11 ml-4 items-center rounded-full transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              :class="enabled ? 'bg-mc-red' : 'bg-gray-200 dark:bg-gray-600'"
            >
              <span
                class="inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ease-in-out"
                :class="enabled ? 'translate-x-6' : 'translate-x-1'"
              ></span>
            </button>
            <p v-if="enabled" class="text-sm mt-2 text-mc-ink dark:text-on-dark">(Click to disable)</p>
            <p v-else class="text-sm mt-2 text-mc-ink dark:text-on-dark">(Click to enable)</p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Report interval -->
          <div class="form-group">
            <label for="xeta-report-interval" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
              Report Interval
              <InfoIcon :content="tooltipContent['Report Interval']" size="xs" />
            </label>
            <input
              id="xeta-report-interval"
              v-model="reportInterval"
              type="number"
              step="1"
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              required
            />
          </div>

          <!-- Unit -->
          <div class="form-group">
            <label for="xeta-unit" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
              Unit
              <InfoIcon :content="tooltipContent['Unit']" size="xs" />
            </label>
            <input
              id="xeta-unit"
              v-model="unit"
              type="text"
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            />
          </div>

          <!-- Offset -->
          <div class="form-group">
            <label for="xeta-offset" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
              Offset
              <InfoIcon :content="tooltipContent['Offset']" size="xs" />
            </label>
            <input
              id="xeta-offset"
              v-model="offset"
              type="number"
              step="any"
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              required
            />
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Minimum electrical value -->
          <div class="form-group">
            <label for="xeta-min-signal" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
              Min signal value
              <InfoIcon :content="tooltipContent['Signal Range min']" size="xs" />
            </label>
            <input
              id="xeta-min-signal"
              v-model="min"
              type="number"
              step="any"
              class="w-1/2 p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              required
            />
            <span class="text-lg text-gray-500 ml-3">{{ electricalOutput }}</span>
          </div>

          <!-- Maximum electrical value -->
          <div class="form-group">
            <label for="xeta-max-signal" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
              Max signal value
              <InfoIcon :content="tooltipContent['Signal Range max']" size="xs" />
            </label>
            <input
              id="xeta-max-signal"
              v-model="max"
              type="number"
              step="any"
              class="w-1/2 p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              required
            />
            <span class="text-lg text-gray-500 ml-3">{{ electricalOutput }}</span>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Minimum measurement value -->
          <div class="form-group">
            <label for="xeta-scale-min" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
              Min Unit Value
              <InfoIcon :content="tooltipContent['Unit min']" size="xs" />
            </label>
            <input
              id="xeta-scale-min"
              v-model="scaleMin"
              type="number"
              step="any"
              class="w-1/2 p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              required
            />
            <span class="text-lg text-gray-500 ml-3">{{ unit }}</span>
          </div>

          <!-- Maximum measurement value -->
          <div class="form-group">
            <label for="xeta-scale-max" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
              Max Unit Value
              <InfoIcon :content="tooltipContent['Unit max']" size="xs" />
            </label>
            <input
              id="xeta-scale-max"
              v-model="scaleMax"
              type="number"
              step="any"
              class="w-1/2 p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              required
            />
            <span class="text-lg text-gray-500 ml-3">{{ unit }}</span>
          </div>
        </div>

        <!-- Description -->
        <div class="form-group">
          <label for="xeta-description" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
            Description
            <InfoIcon :content="tooltipContent['Description']" size="xs" />
          </label>
          <textarea
            id="xeta-description"
            v-model="description"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            rows="3"
          ></textarea>
        </div>

        <!-- Error -->
        <div v-if="error" class="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
          <p class="text-sm">{{ error }}</p>
        </div>

        <!-- Save and close -->
        <div class="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            @click="close"
            class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160"
          >
            Cancel
          </button>

          <div class="flex items-center space-x-4">
            <LoadingAnimation3 v-if="loading" size="30px" />
            <div v-else>
              <button
                v-if="configDetails?.scale?.max !== 0 || configDetails?.scale?.min !== 0 || configDetails?.scale?.offset !== 0"
                type="button"
                @click="removeChannel"
                :disabled="loading"
                class="default-button-off mr-2"
              >
                Disconnect
              </button>

              <button
                type="submit"
                :disabled="loading"
                class="default-button"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>


  </div>
</template>
